import { useEffect, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { JoinTeamView } from '@gg/shared';
import { buttonClasses, PageHeader } from '@/components/ui';
import { ApiRequestError } from '@/lib/api';
import { saveDraft } from '@/lib/checkoutDraft';
import { localName } from '@/lib/formats';
import { useJoinTeamView, useMe } from '@/lib/queries';

/**
 * Invitation link of a teammate (P-21, record 0021 D13): `/teams/join/:code` reads the seat
 * and sends the member into the normal checkout (screen 02) with the format pre-selected at
 * the team's locked tier, the team shown and the email pre-filled. The payment is the usual one
 * (own order, own voucher and insurance); the seat is joined when the webhook confirms it.
 * Screen 07's layout only serves the states where nothing can be paid: dead link, seat already
 * paid, team cancelled or full, format sold out or not on sale.
 */
export function TeamJoin() {
  const { t } = useTranslation();
  const { code = '' } = useParams();
  const view = useJoinTeamView(code);
  const me = useMe();

  if (view.isError) {
    const gone = view.error instanceof ApiRequestError && view.error.status === 410;
    return (
      <Dead
        title={t(gone ? 'join.invalidTitle' : 'error.generic')}
        body={gone ? t('join.invalidBody') : undefined}
      />
    );
  }
  if (!view.data || me.isLoading) return <p className="pt-14 text-center text-ink-muted">…</p>;
  const data = view.data;
  if (data.status === 'invited') {
    return <ToCheckout code={code} view={data} sessionEmail={me.data?.email ?? null} />;
  }
  if (data.status === 'joined') {
    return (
      <Dead title={t('join.joinedTitle')} body={t('join.joinedBody')}>
        <Link to="/login" className={buttonClasses('primary')}>
          {t('join.login')}
        </Link>
      </Dead>
    );
  }
  const key =
    data.status === 'cancelled'
      ? 'cancelled'
      : data.status === 'full'
        ? 'full'
        : data.status === 'sold_out'
          ? 'soldOut'
          : 'notOnSale';
  return (
    <Dead
      title={t(`join.${key}Title`, { team: data.team.name })}
      body={t(`join.${key}Body`, {
        team: data.team.name,
        format: localName(data.format.name, data.format.nameEn),
      })}
    >
      <Link to="/formats" className={buttonClasses('primary')}>
        {t('join.pickFormat')}
      </Link>
    </Dead>
  );
}

/** Stores the team context in the checkout draft, then opens screen 02. */
function ToCheckout({
  code,
  view,
  sessionEmail,
}: {
  code: string;
  view: JoinTeamView;
  sessionEmail: string | null;
}) {
  useEffect(() => {
    // Signed in with the invited address: the email step is skipped, as for the upsell (P-13).
    const matches = sessionEmail !== null && sessionEmail === view.invitedEmail;
    saveDraft({
      formatIds: [view.format.id],
      email: matches ? sessionEmail : view.invitedEmail,
      joinCode: code,
      team: {
        id: view.team.id,
        name: view.team.name,
        formatId: view.format.id,
        captain: view.captain ? (view.captain.firstName ?? view.captain.email) : null,
        priceCents: view.format.priceCents,
        tierName: view.format.tierName,
      },
      teamName: undefined,
      upsell: matches,
    });
  }, [code, view, sessionEmail]);
  return <Navigate to="/register" replace />;
}

function Dead({ title, body, children }: { title: string; body?: string; children?: ReactNode }) {
  return (
    <section className="flex flex-1 flex-col gap-5 pt-14">
      <PageHeader title={title} intro={body} />
      {children}
    </section>
  );
}
